import React, { useCallback, ReactNode, useMemo, createContext, useContext } from 'react';
import { ScheduleFFHelpers } from '@frontend/schedule-helpers';
import { usePreference } from '@frontend/user-preferences';

export interface ZoomContextType {
  zoomLevel: number;
  zoomIn: () => void;
  zoomOut: () => void;

  // derived states
  pixelsPerMinute: number;
  canZoomIn: boolean;
  canZoomOut: boolean;
}

const ZoomContext = createContext<ZoomContextType | undefined>(undefined);

// Safe version that returns default values when provider is not wrapped
export const useZoomSafeContext = (): ZoomContextType => {
  const context = useContext(ZoomContext);

  // If no provider is wrapped, return default values
  if (!context) {
    return {
      zoomLevel: DEFAULT_ZOOM_LEVEL,
      pixelsPerMinute: BASE_PX_PER_MINUTE,
      canZoomIn: false,
      canZoomOut: false,
      zoomIn: () => console.warn('ZoomProvider not found. Please wrap component with ZoomProvider.'),
      zoomOut: () => console.warn('ZoomProvider not found. Please wrap component with ZoomProvider.'),
    };
  }

  return context;
};

interface ZoomProviderProps {
  children: ReactNode;
  defaultZoomLevel?: number;
  minZoom?: number;
  maxZoom?: number;
  canZoomIn?: boolean;
  canZoomOut?: boolean;
}

const ZOOM_LEVELS = [1, 2];
export const DEFAULT_ZOOM_LEVEL = 1;
const BASE_PX_PER_MINUTE = 2;

export const checkIsHalfHourMarkersVisible = (pixelsPerMinute: number): boolean => {
  return pixelsPerMinute > BASE_PX_PER_MINUTE;
};

export const ZoomProvider: React.FC<ZoomProviderProps> = ({ children, minZoom = 1, maxZoom = 2 }) => {
  const { isEnabled } = ScheduleFFHelpers.useCalendarExpandCollapseFeatureFlag();
  const [calendarSettings, setCalendarSettings] = usePreference('ScheduleCalendarSettings');
  const zoomLevel = isEnabled ? calendarSettings?.zoomLevel ?? DEFAULT_ZOOM_LEVEL : DEFAULT_ZOOM_LEVEL;

  const pixelsPerMinute = Math.round(BASE_PX_PER_MINUTE * zoomLevel);

  const setZoomLevel = useCallback(
    (level: number) => {
      const clampedLevel = Math.max(minZoom, Math.min(maxZoom, level));
      setCalendarSettings({ zoomLevel: clampedLevel });
    },
    [minZoom, maxZoom, setCalendarSettings]
  );

  const zoomIn = useCallback(() => {
    const currentIndex = ZOOM_LEVELS.findIndex((level) => level >= zoomLevel);
    if (zoomLevel === minZoom) {
      return;
    }
    setZoomLevel(ZOOM_LEVELS[currentIndex - 1]);
  }, [zoomLevel, setZoomLevel, minZoom]);

  const zoomOut = useCallback(() => {
    const currentIndex = ZOOM_LEVELS.findIndex((level) => level >= zoomLevel);
    if (zoomLevel === maxZoom) {
      return;
    }
    setZoomLevel(ZOOM_LEVELS[currentIndex + 1]);
  }, [zoomLevel, setZoomLevel, maxZoom]);

  const providerValue = useMemo(
    () => ({
      zoomLevel,
      pixelsPerMinute,
      zoomIn,
      zoomOut,

      canZoomIn: zoomLevel < maxZoom,
      canZoomOut: zoomLevel > minZoom,
    }),
    [zoomLevel, pixelsPerMinute, zoomIn, zoomOut, minZoom, maxZoom]
  );

  return <ZoomContext.Provider value={providerValue}>{children}</ZoomContext.Provider>;
};
